import { UUID } from '@cmg/common';
import { AddIcon, Button, Grid, SelectOption, Stack, Typography } from '@cmg/design-system';
import { FormikSelectField } from '@cmg/design-system-formik';
import { FieldArray, FieldArrayRenderProps, useFormikContext } from 'formik';
import React from 'react';

import { OfferingType, SecurityType } from '../../../../../../../../graphql';
import {
  SyndicateIndication_PublishedOfferings_InstrumentPartsFragment,
  SyndicateIndication_PublishedOfferings_Terms_FilingsPartsFragment,
} from '../../../../context/hooks/syndicate/__generated__';
import { IndicationForm_IndicationDetails_Offering_OrderEntryProfilePartsFragment } from '../../../indication-form/components/indication-details/graphql/__generated__';
import { IndicationForm_OfferingPartsFragment } from '../../../indication-form/graphql/__generated__';
import { IndicationFormValues } from '../utils/IndicationSectionValidation';
import {
  createInterestLevelNewValue,
  getCurrencyOptions,
  getInstrumentOptions,
  getTrancheOptions,
} from './IndicationSectionForm.model';
import { InterestLevelTableForm } from './InterestLevelTableForm';
import { InterestLevelTableFormConvertibles } from './InterestLevelTableFormConvertibles';
import InvalidFormAlert from './invalid-form-alert/InvalidFormAlert';

export type Props = Readonly<{
  canEnterTranche?: boolean;
  tranches: IndicationForm_IndicationDetails_Offering_OrderEntryProfilePartsFragment['tranches'];
  instruments: Readonly<SyndicateIndication_PublishedOfferings_InstrumentPartsFragment[]> | null;
  offeringType: IndicationForm_OfferingPartsFragment['type'];
  pricingCurrencyCode: IndicationForm_OfferingPartsFragment['pricingCurrencyCode'];
  securityTypeDisplayName: string;
  isLimitIndicationsOnlyEnabled: boolean;
  isOnlySharesUnitOnIndicationsEnabled: boolean;
  isIndicationPriceAsPercentageParEnabled: boolean;
  filings: Readonly<SyndicateIndication_PublishedOfferings_Terms_FilingsPartsFragment[]> | null;
  securityType: SecurityType | null;
  issuerName: string | null;
  hideMetadataFields?: boolean;
  hideFormAlerts?: boolean;
}>;

const IndicationSectionForm: React.FC<Props> = ({
  tranches,
  instruments,
  offeringType,
  pricingCurrencyCode,
  canEnterTranche = false,
  securityTypeDisplayName,
  isLimitIndicationsOnlyEnabled,
  isOnlySharesUnitOnIndicationsEnabled,
  isIndicationPriceAsPercentageParEnabled,
  filings,
  securityType,
  issuerName,
  hideMetadataFields = false,
  hideFormAlerts = false,
}) => {
  const { values, isSubmitting } = useFormikContext<IndicationFormValues>();

  const selectedTranche = React.useMemo(
    () => tranches.find(({ id }) => id === values.trancheId),
    [tranches, values.trancheId]
  );

  const trancheOptions = React.useMemo<SelectOption<UUID>[]>(
    () => getTrancheOptions(tranches),
    [tranches]
  );
  const instrumentOptions = React.useMemo<SelectOption<UUID>[]>(
    () =>
      getInstrumentOptions({
        issuerName,
        instruments,
        selectedTranche,
        securityTypeDisplayName,
      }),
    [issuerName, instruments, selectedTranche, securityTypeDisplayName]
  );
  const currencyOptions = React.useMemo<SelectOption<string>[]>(
    () => getCurrencyOptions(selectedTranche),
    [selectedTranche]
  );

  const addInterestLevelCallback = React.useCallback(
    (push: FieldArrayRenderProps['push']) => () =>
      push(
        createInterestLevelNewValue(
          values.interestLevels,
          offeringType,
          isLimitIndicationsOnlyEnabled,
          isOnlySharesUnitOnIndicationsEnabled,
          values.selectedInterestUnit
        )
      ),
    [
      isLimitIndicationsOnlyEnabled,
      isOnlySharesUnitOnIndicationsEnabled,
      offeringType,
      values.interestLevels,
      values.selectedInterestUnit,
    ]
  );

  const isDepositaryReceiptSelected = React.useMemo(() => {
    const selectedInstrument = instruments?.find(
      instrument => instrument.id === values.instrumentId
    );
    return !!selectedInstrument?.isDepositaryReceipt;
  }, [instruments, values.instrumentId]);

  return (
    <Stack gap={1}>
      {!hideFormAlerts && <InvalidFormAlert />}

      {!hideMetadataFields && (
        <Grid container pt={1} spacing={2}>
          <Grid item xs={4}>
            <FormikSelectField
              aria-label="tranche"
              name="trancheId"
              label="Tranche"
              disabled={isSubmitting || !canEnterTranche}
              options={trancheOptions}
              showHelperTextInTooltip
              fullWidth
              required
            />
          </Grid>
          <Grid item xs={4}>
            {values.trancheId && (
              <FormikSelectField
                aria-label="demandInstrument"
                name="instrumentId"
                label="Demand Instrument"
                disabled={isSubmitting}
                options={instrumentOptions}
                showHelperTextInTooltip
                fullWidth
                required
              />
            )}
          </Grid>
          <Grid item xs={4}>
            {values.trancheId && (
              <FormikSelectField
                aria-label="demandCurrency"
                name="currencyCode"
                label="Demand Currency"
                disabled={isSubmitting}
                options={currencyOptions}
                showHelperTextInTooltip
                fullWidth
                required
              />
            )}
          </Grid>
        </Grid>
      )}

      <FieldArray
        name="interestLevels"
        render={({ remove, push }) => (
          <Stack gap={1}>
            <Stack gap={2} direction="row" alignItems="center">
              <Typography variant="h4">Interest Levels</Typography>
              <Button
                variant="text"
                startIcon={<AddIcon />}
                disabled={isSubmitting}
                onClick={addInterestLevelCallback(push)}
              >
                Add Interest Level
              </Button>
            </Stack>
            {offeringType === OfferingType.Convertible ? (
              <InterestLevelTableFormConvertibles
                interestLevels={values.interestLevels}
                filings={filings}
                securityType={securityType}
                currencyCode={pricingCurrencyCode}
                onRemove={remove}
                showPercentageOfPar={isIndicationPriceAsPercentageParEnabled}
              />
            ) : (
              <InterestLevelTableForm
                interestLevels={values.interestLevels}
                currencyCode={pricingCurrencyCode}
                onRemove={remove}
                isLimitIndicationsOnlyEnabled={isLimitIndicationsOnlyEnabled}
                isOnlySharesUnitOnIndicationsEnabled={isOnlySharesUnitOnIndicationsEnabled}
                securityType={securityType}
                isDepositoryReceiptEnabled={isDepositaryReceiptSelected}
                offeringType={offeringType}
              />
            )}
          </Stack>
        )}
      />
    </Stack>
  );
};

export default IndicationSectionForm;
